/**
 * Settings → Agent runs → Plugin marketplace (YAG-784): one GitHub repository
 * whose `.claude-plugin/marketplace.json` lists the plugins Wright may load,
 * and which of them this workspace turns on. YAGNI keeps the pointer, never a
 * copy: every run reads the repository fresh and pins the commit it read.
 *
 * Check reads the marketplace through the YAGNI GitHub App and lists its
 * plugins; Save stores the repository and the ticked plugins. A plugin the
 * marketplace points at elsewhere (github, url, npm) is listed but can't be
 * ticked yet. Workspace admins change this; everyone else reads it.
 */
import { useEffect, useState } from "react";
import {
  parseGitHubRepoInput,
  type PluginMarketplacePreviewResponse,
  type PluginMarketplaceResponse,
} from "@yagni/shared";

import { Button } from "../ui/button.js";
import { Input } from "../ui/input.js";
import { ChapterCard, ChapterSection } from "./chapter.js";
import {
  GITHUB_REPO_HINT,
  MARKETPLACE_ADMINS_ONLY,
  MARKETPLACE_CHECK,
  MARKETPLACE_CHECK_FAILED,
  MARKETPLACE_ENABLED,
  MARKETPLACE_FAILED,
  MARKETPLACE_HELP,
  MARKETPLACE_INPUT,
  MARKETPLACE_NONE,
  MARKETPLACE_NO_PLUGINS,
  MARKETPLACE_PLACEHOLDER,
  MARKETPLACE_REMOVE,
  MARKETPLACE_REMOVED,
  MARKETPLACE_SAVE,
  MARKETPLACE_SAVED,
  MARKETPLACE_SECTION,
  MARKETPLACE_UNSUPPORTED,
} from "../lib/agentSourcesCopy.js";
import {
  fetchMarketplace as defaultFetch,
  previewMarketplace as defaultPreview,
  removeMarketplace as defaultRemove,
  saveMarketplace as defaultSave,
  type FetchMarketplace,
  type PreviewMarketplace,
  type RemoveMarketplace,
  type SaveMarketplace,
} from "./agentSourcesApi.js";

export interface PluginMarketplaceSectionProps {
  fetchMarketplace?: FetchMarketplace;
  previewMarketplace?: PreviewMarketplace;
  saveMarketplace?: SaveMarketplace;
  removeMarketplace?: RemoveMarketplace;
}

const LABEL = "block text-sm font-medium text-[var(--text-primary)]";
const HELP = "m-0 text-xs text-[var(--text-tertiary)]";

export function PluginMarketplaceSection({
  fetchMarketplace = defaultFetch,
  previewMarketplace = defaultPreview,
  saveMarketplace = defaultSave,
  removeMarketplace = defaultRemove,
}: PluginMarketplaceSectionProps = {}) {
  // null = loading or the read failed: the section stays out of the way.
  const [state, setState] = useState<PluginMarketplaceResponse | null>(null);
  const [repo, setRepo] = useState("");
  const [preview, setPreview] = useState<PluginMarketplacePreviewResponse | null>(null);
  const [enabled, setEnabled] = useState<string[]>([]);
  const [busy, setBusy] = useState<null | "check" | "save" | "remove">(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    fetchMarketplace()
      .then((read) => {
        if (!live || typeof read?.canEdit !== "boolean") return;
        setState(read);
        setRepo(read.marketplace?.fullName ?? "");
        setEnabled(read.marketplace?.enabledPlugins ?? []);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [fetchMarketplace]);

  if (!state) return null;
  const saved = state.marketplace;

  if (!state.canEdit) {
    return (
      <>
        <ChapterSection label={MARKETPLACE_SECTION} />
        <ChapterCard className="space-y-1 p-4" data-testid="plugin-marketplace-section">
          {saved ? (
            <>
              <p className="m-0 font-mono text-sm text-[var(--text-primary)]">{saved.fullName}</p>
              <p className={HELP}>{MARKETPLACE_ENABLED(saved.enabledPlugins)}</p>
            </>
          ) : (
            <p className="m-0 text-sm text-[var(--text-secondary)]">{MARKETPLACE_NONE}</p>
          )}
          <p className={HELP}>{MARKETPLACE_ADMINS_ONLY}</p>
        </ChapterCard>
      </>
    );
  }

  async function check() {
    setError(null);
    setNotice(null);
    const parsed = parseGitHubRepoInput(repo);
    if (!parsed) {
      setError(GITHUB_REPO_HINT);
      return;
    }
    setBusy("check");
    try {
      // Keep a saved branch when the same repository is checked again.
      const sameRepo = saved && saved.fullName.toLowerCase() === parsed.fullName.toLowerCase();
      const read = await previewMarketplace({
        repo: parsed.fullName,
        ...(sameRepo && saved.ref ? { ref: saved.ref } : {}),
      });
      setPreview(read);
      const supported = new Set(read.plugins.filter((p) => p.supported).map((p) => p.name));
      setEnabled((current) => current.filter((name) => supported.has(name)));
    } catch (err) {
      setPreview(null);
      setError(err instanceof Error && err.message ? err.message : MARKETPLACE_CHECK_FAILED);
    } finally {
      setBusy(null);
    }
  }

  async function save() {
    if (!preview) return;
    setError(null);
    setNotice(null);
    setBusy("save");
    try {
      const body = { repo: preview.fullName, ref: preview.ref, enabledPlugins: enabled };
      await saveMarketplace(body);
      setState({ canEdit: true, marketplace: { fullName: preview.fullName, ref: preview.ref, enabledPlugins: enabled } });
      setRepo(preview.fullName);
      setNotice(MARKETPLACE_SAVED);
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : MARKETPLACE_FAILED);
    } finally {
      setBusy(null);
    }
  }

  async function remove() {
    setError(null);
    setNotice(null);
    setBusy("remove");
    try {
      await removeMarketplace();
      setState({ canEdit: true, marketplace: null });
      setRepo("");
      setPreview(null);
      setEnabled([]);
      setNotice(MARKETPLACE_REMOVED);
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : MARKETPLACE_FAILED);
    } finally {
      setBusy(null);
    }
  }

  const toggle = (name: string, on: boolean) =>
    setEnabled((current) => (on ? [...current.filter((n) => n !== name), name] : current.filter((n) => n !== name)));

  return (
    <>
      <ChapterSection label={MARKETPLACE_SECTION} />
      <ChapterCard className="space-y-4 p-4" data-testid="plugin-marketplace-section">
        <div className="space-y-1">
          <label htmlFor="plugin-marketplace-repo" className={LABEL}>
            {MARKETPLACE_INPUT}
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              id="plugin-marketplace-repo"
              value={repo}
              placeholder={MARKETPLACE_PLACEHOLDER}
              spellCheck={false}
              className="max-w-sm font-mono"
              onChange={(event) => {
                setRepo(event.target.value);
                setPreview(null);
                setNotice(null);
              }}
            />
            <Button variant="secondary" size="sm" disabled={busy !== null || !repo.trim()} loading={busy === "check"} onClick={() => void check()}>
              {MARKETPLACE_CHECK}
            </Button>
          </div>
          <p className={HELP}>{MARKETPLACE_HELP}</p>
          {saved && !preview && <p className={HELP}>{MARKETPLACE_ENABLED(saved.enabledPlugins)}</p>}
        </div>

        {preview && (
          <fieldset className="m-0 space-y-2 border-0 p-0" data-testid="plugin-marketplace-plugins">
            <legend className="sr-only">Plugins</legend>
            {preview.plugins.length === 0 && <p className={HELP}>{MARKETPLACE_NO_PLUGINS}</p>}
            {preview.plugins.map((plugin) => (
              <label key={plugin.name} className="flex items-start gap-2 text-sm text-[var(--text-primary)]">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={plugin.supported && enabled.includes(plugin.name)}
                  disabled={!plugin.supported || busy !== null}
                  onChange={(event) => toggle(plugin.name, event.target.checked)}
                />
                <span className="min-w-0">
                  <span className="font-mono">{plugin.name}</span>
                  {!plugin.supported && <span className="text-[var(--text-tertiary)]"> · {MARKETPLACE_UNSUPPORTED}</span>}
                  {plugin.description && <span className={`block ${HELP}`}>{plugin.description}</span>}
                </span>
              </label>
            ))}
          </fieldset>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <Button disabled={!preview || busy !== null} loading={busy === "save"} onClick={() => void save()}>
            {MARKETPLACE_SAVE}
          </Button>
          {saved && (
            <Button variant="ghost" disabled={busy !== null} loading={busy === "remove"} onClick={() => void remove()}>
              {MARKETPLACE_REMOVE}
            </Button>
          )}
          {error ? (
            <span role="alert" className="text-sm text-[var(--error)]">
              {error}
            </span>
          ) : notice ? (
            <span role="status" className="text-sm text-[var(--text-secondary)]">
              {notice}
            </span>
          ) : null}
        </div>
      </ChapterCard>
    </>
  );
}
